import React, { useEffect, useState } from "react";
import { 
  Layout, 
  LayoutContent, 
  LayoutBody,
  LayoutHeader 
} from "./layout";
import { 
  Sidebar, 
  SidebarHeader, 
  SidebarContent, 
  SidebarFooter 
} from "./sidebar";
import { CompanySwitcher } from "./dropdown";
import SiderComponent from "../shared/SiderComponent";
import HeaderComponent from "../shared/HeaderComponent";
import MaitaiLogo from "../../assets/maitai_logo_full.svg";
import MaitaiLogoDark from "../../assets/maitai_logo_dark_transparent.svg";
import { useTheme } from "../../contexts/ThemeContext";

interface AppLayoutProps {
  children: React.ReactNode;
  companies?: { company_name: string; key_value: string }[];
  setMeta?: (meta: any) => void;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ 
  children, 
  companies = [], 
  setMeta 
}) => {
  const { theme } = useTheme();
  
  // Initialize both company name and API key from localStorage
  const savedApiKey = localStorage.getItem("selectedCompanyApiKey");
  const savedCompanyName = localStorage.getItem("selectedCompanyName");
  const baseApiKey = localStorage.getItem("baseCompanyApiKey");
  const baseCompanyName = localStorage.getItem("baseCompanyName");
  
  if (!baseApiKey && savedApiKey && savedCompanyName) {
    localStorage.setItem("baseCompanyApiKey", savedApiKey);
    localStorage.setItem("baseCompanyName", savedCompanyName);
  }

  const [companyName, setCompanyName] = useState<string>(() => {
    // If we have both saved values, use the company name
    if (savedApiKey && savedCompanyName) {
      return savedCompanyName;
    }
    // If we don't have both, reset both to ensure consistency
    if (savedApiKey || savedCompanyName) {
      localStorage.removeItem("selectedCompanyApiKey");
      localStorage.removeItem("selectedCompanyName");
    }
    return "Change Environment";
  });

  useEffect(() => {
    // When companies are loaded, try to restore the saved company
    if (companies?.length > 0 && savedApiKey) {
      const savedCompany = companies.find((c) => c.key_value === savedApiKey);
      if (savedCompany) {
        setCompanyName(savedCompany.company_name);
        if (setMeta) {
          setMeta({ api_key: savedApiKey });
        }
      } else {
        // If the saved company is not found in the list, clear saved data
        localStorage.removeItem("selectedCompanyApiKey");
        localStorage.removeItem("selectedCompanyName");
        setCompanyName("Change Environment");
      }
    }
  }, [companies, setMeta, savedApiKey]);

  const handleCompanyChange = (apiKey: string, companyName: string) => {
    if (setMeta) {
      setMeta({ api_key: apiKey });
      localStorage.setItem("selectedCompanyApiKey", apiKey);
    }
    setCompanyName(companyName);
    localStorage.setItem("selectedCompanyName", companyName);
  };

  const showCompanySwitcher = 
    (companies.length > 0 || baseCompanyName === "Maitai");

  return (
    <Layout>
      {/* Sidebar */}
      <Sidebar width={256}>
        {/* Logo Section */}
        <SidebarHeader>
          <img
            src={theme === "dark" ? MaitaiLogoDark : MaitaiLogo}
            alt="Maitai Logo"
            className="h-8 w-auto"
          />
        </SidebarHeader>

        {/* Navigation */}
        <SidebarContent>
          <SiderComponent />
        </SidebarContent>

        {/* Company Switcher */}
        {showCompanySwitcher && (
          <SidebarFooter>
            <CompanySwitcher
              currentCompany={companyName}
              companies={companies}
              onCompanyChange={handleCompanyChange}
            />
          </SidebarFooter>
        )}
      </Sidebar>

      {/* Main Content */}
      <LayoutContent>
        {/* Header */}
        <LayoutHeader>
          <HeaderComponent />
        </LayoutHeader>

        {/* Page Content */}
        <LayoutBody>
          {children}
        </LayoutBody>
      </LayoutContent>
    </Layout>
  );
};